import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  SxProps,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { usePaginatedAutocomplete } from "../../hooks/usePaginatedAutocomplete";
import { PaginatedResponse } from "../../types/interfaces/app/PaginatedResponse";

interface PaginatedAutocompleteProps<T extends { id: number; name: string; code?: string }> {
  name: string;
  label: string;
  fetchFn: (search: string, offset: number, limit: number) => Promise<PaginatedResponse<T>>;
  fetchOneFn?: (id: number) => Promise<T>;
  isEditing: boolean;
  isSaving: boolean;
  disabled?: boolean;
  formStyles?: SxProps;
  limit?: number;
  multiple?: boolean;
}

export function PaginatedAutocomplete<T extends { id: number; name: string; code?: string }>({
  name,
  label,
  fetchFn,
  fetchOneFn,
  isEditing,
  isSaving,
  disabled = false,
  formStyles,
  limit = 50,
  multiple = false,
}: Readonly<PaginatedAutocompleteProps<T>>) {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const { options, loading, onInputChange, loadMore, reset } =
    usePaginatedAutocomplete({ fetchFn, limit });
  const [viewOptions, setViewOptions] = useState<T[]>([]);

  const fieldValue = useWatch({ control, name: name as any });

  useEffect(() => {
    if (!fetchOneFn) return;

    const ids: number[] = multiple
      ? Array.isArray(fieldValue)
        ? fieldValue
        : []
      : fieldValue
        ? [fieldValue]
        : [];

    const knownIds = new Set([...options.map((o) => o.id), ...viewOptions.map((o) => o.id)]);
    const missingIds = ids.filter((id) => !knownIds.has(id));

    if (missingIds.length === 0) return;

    Promise.all(missingIds.map((id) => fetchOneFn(id).catch(() => null))).then((fetched) => {
      const valid = fetched.filter((o) => o !== null) as T[];
      if (valid.length > 0) {
        setViewOptions((prev) => [...prev, ...valid]);
      }
    });
  }, [fieldValue, fetchOneFn, multiple]);

  const getDisplayInfo = (opt: T): { label: string; hint: string } => {
    if (opt.name)
      return {
        label: opt.name,
        hint: t("captures.capture.general.identification.name"),
      };
    if ((opt as any).entityDesignation)
      return {
        label: (opt as any).entityDesignation,
        hint: t("captures.capture.general.identification.entity-desig"),
      };
    return {
      label: String(opt.id),
      hint: "ID",
    };
  };

  const getLabel = (opt: T) => {
    const { label } = getDisplayInfo(opt);
    return opt.code ? `(${opt.code}) ${label}` : label;
  };

  const handleOpen = () => {
    reset();
    onInputChange("");
  };

  const debouncedInputChange = useDebouncedCallback(onInputChange, 300);

  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field, fieldState }) => {
        const allOptions = [
          ...viewOptions.filter((v) => !options.some((o) => o.id === v.id)),
          ...options,
        ];

        const selectedIds: number[] = multiple
          ? Array.isArray(field.value)
            ? field.value
            : []
          : field.value
            ? [field.value]
            : [];

        const selectedOptions = selectedIds
          .map((id) => allOptions.find((o) => o.id === id))
          .filter((o): o is T => !!o);

        const autocompleteValue = multiple ? selectedOptions : (selectedOptions[0] ?? null);
        const hint = !multiple && selectedOptions[0] ? getDisplayInfo(selectedOptions[0]).hint : undefined;

        const handleRemove = (id: number) => {
          if (multiple) {
            field.onChange(selectedIds.filter((sid) => sid !== id));
          } else {
            field.onChange(null);
          }
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Autocomplete
              fullWidth
              multiple={multiple}
              options={allOptions}
              value={autocompleteValue as any}
              loading={loading}
              getOptionLabel={getLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(x) => x}
              onOpen={handleOpen}
              onInputChange={(_, value, reason) => {
                if (reason === "input") debouncedInputChange(value);
              }}
              onChange={(_, newValue) => {
                if (multiple) {
                  field.onChange((newValue as T[]).map((o) => o.id));
                } else {
                  field.onChange((newValue as T | null)?.id ?? null);
                }
              }}
              disabled={!isEditing || isSaving || disabled}
              noOptionsText={loading ? t("generic.loading") : t("generic.no-options")}
              renderTags={multiple ? () => null : undefined}
              ListboxProps={{
                onScroll: (e) => {
                  const el = e.currentTarget;
                  if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
                    loadMore();
                  }
                },
              }}
              renderOption={(props, option) => {
                const { label, hint } = getDisplayInfo(option);
                const lbl = option.code ? `(${option.code}) ${label}` : label;
                const { key, ...rest } = props;
                return (
                  <li key={option.id} {...rest}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                      <span>{lbl}</span>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {hint}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant={isEditing ? "outlined" : "standard"}
                  sx={formStyles}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress size={16} />}
                        {hint && (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">
                              {hint}
                            </Typography>
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {multiple && selectedOptions.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  maxHeight: 320,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {selectedOptions.map((option) => {
                  const { label, hint } = getDisplayInfo(option);
                  const lbl = option.code ? `(${option.code}) ${label}` : label;
                  return (
                    <Paper
                      key={option.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        px: 2,
                        borderRadius: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {lbl}
                        </Typography>
                        {hint && (
                          <Typography variant="caption" color="text.secondary">
                            {hint}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => handleRemove(option.id)}
                        disabled={!isEditing || isSaving || disabled}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
}